import { create } from 'zustand'

import { isDebug } from '@core/debug'
import type { GetState, NamedSet, UseBoundStore } from '@core/zustand'
import { createContext } from '@core/zustand'
import { withLenses } from '@core/zustand/lens'

import { actions } from './actions'
import { addDevtool } from './helpers'
import { initialState } from './initialState'
import type { IWidgetState, StateCreator } from './types'

/**
 *
 */
export function createInitStateCreator(
  extraState?: (
    set?: NamedSet<IWidgetState>,
    get?: GetState<IWidgetState>,
  ) => Partial<IWidgetState>,
) {
  return (
    set: NamedSet<IWidgetState>,
    get: GetState<IWidgetState>,
  ): IWidgetState => {
    const { actions: customActions, ...customState } =
      extraState?.(set, get) ?? {}

    return {
      ...initialState,
      ...customState,

      actions: {
        ...actions(set, get),
        ...customActions,
      },
    }
  }
}

/**
 * @param extraState Custom state to be merged with initial state.
 * @returns A zustand store
 */
export const createStore = (
  name: string,
  extraState?: (
    set?: NamedSet<IWidgetState>,
    get?: GetState<IWidgetState>,
  ) => Partial<IWidgetState>,
): UseBoundStore<IWidgetState> => {
  let createState: StateCreator = createInitStateCreator(extraState)
  createState = withLenses<IWidgetState>(createState) as StateCreator

  // Note: devtool must be configured before other middleware.
  if (isDebug()) {
    createState = addDevtool(createState, name)
  }

  const store = create<IWidgetState>(createState)

  if (window.Cypress) {
    window.Cypress['widgetStore'] = store
  }

  return store
}

export const { Provider, useStore, useStoreApi } = createContext<IWidgetState>()
